import React, { useState, useMemo, Fragment, useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import Papa from 'papaparse';

import Box from '@mui/material/Box';
import MuiIconButton from '@mui/material/IconButton';
import AddIcon from '@mui/icons-material/Add';
import MenuIcon from '@mui/icons-material/Menu';
import FilterIcon from '@mui/icons-material/FilterListOutlined';
import FiltersIcon from '@mui/icons-material/FilterAltOutlined';
import CloseIcon from '@mui/icons-material/Close';
import UploadIcon from '@mui/icons-material/Upload';
import DeleteIcon from '@mui/icons-material/DeleteOutline';

import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';

import ProductsTable from './ProductsTable';
import ProductsTableMobile from './ProductsTableMobile';
import FilterProductsModal from './FilterProductsModal';
import UploadProductsModal from './UploadProductsModal';
import ProductModal from './ProductModal';
import ConfirmationModal from '../modals/ConfirmationModal';
import LoadScreen from '../load-screen';
import Alert from '../shared/Alert';
import Search from '../shared/Search';
import Button from '../shared/Button';
import IconButton from '../shared/IconButton';

import {
  fetchProducts,
  updateProduct,
  deleteProduct,
  createProduct,
  createProductTag,
} from '../../actions/productsActions';
import { toggleSidebar } from '../../actions/sidebarActions';

import { getComparator, stableSort } from '../../util/table';

import './products.scss';

const ProductsTableContainer = (props) => {
  const {
    products = [],
    distributors,
    createProduct,
    updateProduct,
    deleteProduct,
    fetchProducts,
    withButton,
    toggleSidebar,
    countries,
    productTags = [],
    createProductTag,
  } = props;

  const theme = useTheme();

  const [search, setSearch] = useState('');

  const [loading, setLoading] = useState(false);
  const [fetching] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const [showCreateModal, toggleCreateModal] = useState(false);
  const [showUploadModal, toggleUploadModal] = useState(false);
  const [showUpdateModal, toggleUpdateModal] = useState(false);
  const [showDeleteModal, toggleDeleteModal] = useState(false);
  const [showBulkDeleteModal, toggleBulkDeleteModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  const [selectedId, setSelectedId] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedProductIds, setSelectedProductIds] = useState([]);

  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState(null);

  const [showFilterModal, toggleFilterModal] = useState(false);
  const [filterState, setFilterState] = useState({ countries: [], tags: [] });

  const onEditClick = (product) => {
    setSelectedProduct(product);
    toggleUpdateModal(true);
  };

  const onProductCreate = async (formObj) => {
    try {
      setLoading(true);

      let body = formObj;

      if (formObj?.tags?.length) {
        const tags = await Promise.all(formObj.tags.map(async (tag) => {
          const _tag = productTags.find((t) => t.label === tag.label);
          if (!_tag) {
            const res = await createProductTag(tag);
            return { id: res.id, label: tag?.label || '' }
          }
          return tag;
        }));
        body.tags = tags || [];
      }

      await createProduct(body);
      await fetchProducts();
      setSuccessMessage('New product successfully created');
      toggleCreateModal(false);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const onProductUpdate = async (formObj) => {
    if (selectedProduct) {
      try {
        setLoading(true);

        let body = formObj;

        if (formObj?.tags?.length) {
          const tags = await Promise.all(formObj.tags.map(async (tag) => {
            const _tag = productTags.find((t) => t.label === tag.label);
            if (!_tag) {
              const res = await createProductTag(tag);
              return { id: res.id, label: tag?.label || '' }
            }
            return tag;
          }));
          body.tags = tags || [];
        }

        await updateProduct(selectedProduct?.id, body);
        await fetchProducts();
        setSuccessMessage('Product successfully updated');
        toggleUpdateModal(false);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    }
  };

  const onDeleteClick = async (id) => {
    setSelectedId(id);
    toggleDeleteModal(true);
  };

  useEffect(() => {
    setSelectedProductIds((prev) => prev.filter((id) => products.some((product) => product.id === id)));
  }, [products]);

  const onDeleteProduct = async () => {
    if (selectedId) {
      try {
        setDeleteLoading(true)
        await deleteProduct(selectedId);
        await fetchProducts();
        toggleDeleteModal(false);
        setSelectedProductIds((prev) => prev.filter((id) => id !== selectedId));
        setSelectedId(null);
        setSuccessMessage('Product successfully deleted');
      } catch (err) {
        console.error(err);
      } finally {
        setDeleteLoading(false);
      }
    }
  };

  const onToggleProductRow = (productId) => {
    if (!productId) {
      return;
    }

    setSelectedProductIds((prev) => (
      prev.includes(productId)
        ? prev.filter((id) => id !== productId)
        : [...prev, productId]
    ));
  };

  const onToggleAllProductRows = (checked, productIds) => {
    if (checked) {
      setSelectedProductIds((prev) => Array.from(new Set([...prev, ...productIds])));
      return;
    }

    setSelectedProductIds((prev) => prev.filter((id) => !productIds.includes(id)));
  };

  const onBulkDeleteProducts = async () => {
    if (!selectedProductIds.length) {
      return;
    }

    try {
      setDeleteLoading(true);
      await Promise.all(selectedProductIds.map((id) => deleteProduct(id)));
      await fetchProducts();
      toggleBulkDeleteModal(false);
      setSuccessMessage(
        `${selectedProductIds.length} product${selectedProductIds.length === 1 ? '' : 's'} successfully deleted`
      );
      setSelectedProductIds([]);
    } catch (err) {
      console.error(err);
    } finally {
      setDeleteLoading(false);
    }
  };

  const onSubmitFilters = (filters) => {
    setFilterState({
      countries: filters.countries || [],
      tags: filters.tags || [],
    });
    toggleFilterModal(false);
  };

  const onRemoveFilterItem = (key, value) => {
    setFilterState({
      ...filterState,
      [key]: filterState[key].filter((v) => v !== value),
    })
  }

  const uploadProducts = async (list) => {
    try {
      setLoading(true);
      for (const product of list) {
        const sku = product[0] ? product[0].trim() : '';
        const description = product[1] ? product[1].trim() : '';

        if (sku && description) {
          await createProduct({ sku, description, tags: [] });
        }
      }
      await fetchProducts();
      toggleUploadModal(false);
      setSuccessMessage('Products successfully uploaded');
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  const onUpload = (files) => {
    Papa.parse(files[0], {
      header: false,
      skipEmptyLines: true,
      complete: async (results) => {
        if (results && results.data) {
          await uploadProducts(results.data);
        }
      },
    });
  };

  const filteredProducts = useMemo(() => {
    let filtered = products;

    if (search) {
      const q = search.toLowerCase();
      filtered = products.filter((p) => p?.description?.toLowerCase()?.includes(q) || p?.sku?.toLowerCase()?.includes(q));
    }

    if (filterState?.countries?.length) {
      filtered = filtered?.filter((p) => filterState?.countries?.includes(p.country));
    }

    if (filterState?.tags?.length) {
      filtered = filtered?.filter((p) => p.tags?.some((tag) => filterState?.tags?.find((t) => t.id === tag.id)));
    }

    return filtered;
  }, [products, search, filterState]);

  const list = useMemo(() => {
    let result = filteredProducts;

    if (orderBy) {
      result = stableSort(
        result,
        getComparator(order, orderBy)
      );
    }

    const _page = page - 1;

    return result
      .slice(
        _page * rowsPerPage,
        _page * rowsPerPage + rowsPerPage
      );
  }, [filteredProducts, page, rowsPerPage, order, orderBy]);

  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <>
      {fetching && <LoadScreen />}
      <Box className="cases-table-container">
        <Box className="table-title-container">
          <Box className="flex-1">
            <Box className="d-flex">
              {withButton && (
                <Box sx={{ display: { sm: 'none' } }}>
                  <Box className="m-r-sm">
                    <MuiIconButton
                      edge="start"
                      color="primary"
                      aria-label="menu"
                      onClick={toggleSidebar}
                    >
                      <MenuIcon />
                    </MuiIconButton>
                  </Box>
                </Box>
              )}

              <Box>
                <Box className="table-title">
                  Products
                  <Box className="badge">{products.length}</Box>
                </Box>

                {!isMobile && (
                  <Box className='table-subtitle'>
                    Add or manage products here
                  </Box>
                )}
              </Box>
            </Box>
          </Box>
          <Box className='d-flex'>
            <Box sx={{ display: { xs: 'none', sm: 'block' }, width: '240px', mr: 2 }}>
              <Search
                value={search}
                placeholder="Search"
                onChange={(e) => {
                  setPage(1);
                  setSearch(e.target.value);
                }}
              />
            </Box>
            <IconButton
              onClick={() => toggleFilterModal(true)}
              icon={<FilterIcon />}
              sx={{ mr: 2 }}
            />
            <IconButton
              onClick={() => toggleUploadModal(true)}
              icon={<UploadIcon color='primary' />}
              sx={{ mr: 2, display: { xs: 'none', sm: 'flex' } }}
            />
            {!!selectedProductIds.length && (
              <Button
                text={`Delete Selected (${selectedProductIds.length})`}
                onClick={() => toggleBulkDeleteModal(true)}
                icon={<DeleteIcon />}
                type='secondary'
                sx={{ mr: 2, display: { xs: 'none', md: 'flex' } }}
              />
            )}
            <Button
              text='Add Product'
              onClick={() => toggleCreateModal(true)}
              icon={<AddIcon color='white' />}
            />
          </Box>
        </Box>
        {products.length ? (
          <Fragment>
            <Box sx={{ display: { xs: 'none', md: 'block' } }}>
              {filterState &&
                (Object.keys(filterState)?.some((key) => filterState[key]?.length)
                  && (
                    <div className="filter-list">
                      <FiltersIcon color="secondary" />
                      {Object.keys(filterState).map((key) => (
                        <div key={key} className="filter-list-item-container">
                          {filterState[key].map((value) => (
                            <div key={`${key}-${value}`} className="filter-list-item">
                              <div />
                              <div className="filter-list-item-value">{key ==='tags' ? value?.label : value}</div>
                              <CloseIcon color="secondary" onClick={() => onRemoveFilterItem(key, value)} />
                            </div>
                          ))}
                        </div>
                      ))}
                    </div>
                  ))
              }
              <ProductsTable
                products={list}
                distributors={distributors}
                onEditClick={onEditClick}
                onDeleteClick={onDeleteClick}
                page={page}
                setPage={setPage}
                rowsPerPage={rowsPerPage}
                setRowsPerPage={setRowsPerPage}
                totalRows={filteredProducts?.length}
                order={order}
                setOrder={setOrder}
                orderBy={orderBy}
                setOrderBy={setOrderBy}
                selectedRowIds={selectedProductIds}
                onToggleRow={onToggleProductRow}
                onToggleAllRows={onToggleAllProductRows}
              />
            </Box>

            <Box sx={{ display: { xs: 'block', md: 'none' } }}>
              <ProductsTableMobile products={list} />
            </Box>
          </Fragment>
        ) : (
          <Box className="empty-block">No Products</Box>
        )}
      </Box>

      <ProductModal
        open={showCreateModal}
        onClose={() => toggleCreateModal(false)}
        onSubmit={onProductCreate}
        countries={countries}
        loading={loading}
        productTags={productTags}
      />

      {!!selectedProduct && (
        <ProductModal
          open={showUpdateModal}
          onClose={() => {
            toggleUpdateModal(false);
            setSelectedProduct(undefined);
          }}
          onSubmit={onProductUpdate}
          countries={countries}
          mode='update'
          initialValues={{ ...selectedProduct, tags: selectedProduct?.tags || [] }}
          loading={loading}
          productTags={productTags}
        />
      )}

      <UploadProductsModal
        open={showUploadModal}
        onClose={() => toggleUploadModal(false)}
        onUpload={onUpload}
        loading={loading}
      />

      <FilterProductsModal
        filter={filterState}
        open={showFilterModal}
        onClose={() => toggleFilterModal(false)}
        onSubmit={onSubmitFilters}
        countries={countries}
        productTags={productTags}
      />

      <ConfirmationModal
        open={showDeleteModal}
        onClose={() => {
          toggleDeleteModal(false);
          setSelectedId(null);
        }}
        onSubmit={onDeleteProduct}
        title="Delete Product?"
        text="Are you sure you want to delete this product? This action cannot be reversed."
        closeText="Cancel"
        submitText="Delete"
        variant="error"
        loading={deleteLoading}
      />

      <ConfirmationModal
        open={showBulkDeleteModal}
        onClose={() => toggleBulkDeleteModal(false)}
        onSubmit={onBulkDeleteProducts}
        title="Delete Selected Products?"
        text={`Are you sure you want to delete ${selectedProductIds.length} selected product${selectedProductIds.length === 1 ? '' : 's'}? This action cannot be reversed.`}
        closeText="Cancel"
        submitText="Delete Selected"
        variant="error"
        loading={deleteLoading}
      />

      <Alert
        variant="success"
        message={successMessage}
        open={!!successMessage}
        onClose={() => setSuccessMessage('')}
      />
    </>
  );
};

ProductsTableContainer.propTypes = {
  products: PropTypes.array,
  distributors: PropTypes.array,
};

const mapStateToProps = (state) => {
  return {
    countries: state.countries.list,
    productTags: state.products.tags,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    fetchProducts: () => dispatch(fetchProducts()),
    createProduct: (productData) => dispatch(createProduct(productData)),
    updateProduct: (id, productData) => dispatch(updateProduct(id, productData)),
    deleteProduct: (id) => dispatch(deleteProduct(id)),
    createProductTag: (tag) => dispatch(createProductTag(tag)),
    toggleSidebar: () => dispatch(toggleSidebar()),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(ProductsTableContainer);
